<template>
    <div class="translate-input" :class="[type]">

        <div class="input-container">
            <b-form-group :label="label | tr" :class="{ 'inline': inline, 'mb-0': noMargin }">
                <template v-if="tr(label) || $slots['label-info']" #label>
                    {{ label | tr }}
                    <slot name="label-info"></slot>
                </template>

                <div class="locale-inputs">
                    <div class="locale-input">
                        <b-input-group :size="size">
                            <slot name="prepend"></slot>
                            <e-btn-language v-if="locales.length > 1" :size="size" @input="setPreviewLanguage" :value="currentLanguage.locale" :locales="locales.map(l => l.locale)" />
                            <b-input-group-text v-else class="flag-container">
                                <b-img v-if="currentLanguage.flag" :src="currentLanguage.flag" :alt="currentLanguage.locale" />
                            </b-input-group-text>

                            <template v-if="type === 'textarea'">
                                <b-textarea :value="getLocaleValue(currentLanguage.locale)"
                                    autocomplete="off" @input="onInput(currentLanguage.locale, $event)"
                                    @change="$emit('change', $event)"
                                    @click="openModal"
                                    :placeholder="placeholderForLocale(currentLanguage)" :readonly="readonly" no-resize
                                    :disabled="disabled" :style="{ 'min-height': height }"
                                    :state="localeState(currentLanguage.locale)" :maxlength="maxlength" />
                            </template>
                            <template v-else>
                                <b-input :size="size" :value="getLocaleValue(currentLanguage.locale)"
                                    autocomplete="off" @input="onInput(currentLanguage.locale, $event)"
                                    :readonly="readonly"
                                    @click="openModal"
                                    @change="$emit('change', $event)" :placeholder="placeholderForLocale(currentLanguage)"
                                    :state="localeState(currentLanguage.locale)" :maxlength="maxlength" :disabled="disabled" />
                            </template>

                            <slot name="append"></slot>
                            <slot name="autocomplete" v-if="autoCompletion">
                                <b-btn class="w-auto" @click="showAutoCompletion" v-model="autoCompletionVisible"
                                    :title="'Browse suggestions|Parcourir les suggestions' | tr">
                                    <i class="fas fa-caret-down"/>
                                </b-btn>
                            </slot>

<!--                            <b-btn v-if="showModalToggle" @click.stop="openModal" variant="light" :size="size" class="modal-toggle-btn"-->
<!--                                :title="'Edit translations (Ctrl+Click)|Modifier les traductions (Ctrl+Clique)' | tr" v-b-tooltip>-->
<!--                                <i class="fas fa-pen-field"/>-->
<!--                            </b-btn>-->

                            <!-- <b-btn v-if="extended && autoTranslate && locale.locale !== sourceLanguage"
                                @click.stop="translateLocale(locale)" variant="light"
                                :title="'Generate translation|Generer la traduction' | tr" v-b-tooltip
                                :disabled="translatingLocales[locale.locale]"
                                class="translate-btn">
                                <i v-if="translatingLocales[locale.locale]" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-language"></i>
                            </b-btn> -->
                        </b-input-group>
                    </div>
                </div>

                <div class="form-help">
                    <slot name="form-help"></slot>
                </div>
            </b-form-group>
        </div>

        <b-modal v-if="extendedModalEnabled" v-model="extended" size="lg" :title="'Translations|Traductions' | tr"
                 scrollable body-bg-variant="white" footer-bg-variant="light" class="translation-modal" no-fade>

            <div class="text-center">
                <b-badge variant="light" size="lg" pill>
                    <div v-if="autoTranslate && locales.length > 1" class="px-3 py-2">
                        <div class="d-flex align-items-center">
                            <div class="label mb-0 mr-2" v-tr>Translate From|Traduire de</div>
                            <e-btn-language v-model="sourceLanguage" :locales="locales.map(l => l.locale)" show-label/>
                        </div>
                    </div>
                </b-badge>
            </div>

            <div class="locale-inputs">
                <div v-for="(locale, index) in locales" :key="'modal-' + locale.locale" class="locale-input">
                    <b-form-group :label="`${locale.name} (${locale.locale})`">
                        <b-input-group>
                            <slot v-if="index === 0" name="prepend"></slot>
                            <b-input-group-text class="flag-container">
                                <b-img v-if="locale.flag" :src="locale.flag" :alt="locale.locale" />
                            </b-input-group-text>

                            <template v-if="type === 'textarea'">
                                <b-textarea :value="getLocaleValue(locale.locale)"
                                    autocomplete="off" @input="onInput(locale.locale, $event)"
                                            :autofocus="locale.locale === sourceLanguage"
                                    @keyup.ctrl.enter="extended = false" @change="$emit('change', $event)"
                                    :placeholder="placeholderForLocale(locale)" :readonly="readonly"
                                    :disabled="disabled" :style="{ 'min-height': expandedHeight }"
                                    :state="localeState(locale.locale)" :maxlength="maxlength">
                                </b-textarea>

                                <div v-if="hasTranslationCheckPreview(locale.locale)" class="translation-check-preview">
                                    <b-textarea :value="translationCheckPreviews[locale.locale]"
                                                autocomplete="off" readonly disabled
                                                :style="{ 'min-height': height }" />
                                </div>
                            </template>
                            <template v-else>
                                <b-input :value="getLocaleValue(locale.locale)"
                                         autocomplete="off" @input="onInput(locale.locale, $event)"
                                         :autofocus="locale.locale === sourceLanguage"
                                         @keyup.enter="extended = false" :readonly="readonly"
                                         @change="$emit('change', $event)" :placeholder="placeholderForLocale(locale)"
                                         :state="localeState(locale.locale)" :maxlength="maxlength" :disabled="disabled">
                                </b-input>

                                <div v-if="hasTranslationCheckPreview(locale.locale)" class="translation-check-preview">
                                    <b-input :value="translationCheckPreviews[locale.locale]"
                                             autocomplete="off" readonly disabled />
                                </div>
                            </template>

                            <slot v-if="index === 0" name="append"></slot>
                            <slot v-if="index === 0 && autoCompletion" name="autocomplete">
                                <b-btn class="w-auto" @click="showAutoCompletion" v-model="autoCompletionVisible"
                                    :title="'Browse suggestions|Parcourir les suggestions' | tr">
                                    <i class="fas fa-caret-down"></i>
                                </b-btn>
                            </slot>

                            <b-btn v-if="autoTranslate && locale.locale !== sourceLanguage"
                                @click.stop="translateLocale(locale)" variant="light"
                                :title="'Generate translation|Générer la traduction' | tr" v-b-tooltip
                                :disabled="translatingLocales[locale.locale]"
                                class="translate-btn">
                                <i v-if="translatingLocales[locale.locale]" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-language"></i>
                            </b-btn>
                        </b-input-group>
                    </b-form-group>
                </div>
            </div>

            <template #modal-footer>
                <b-dropdown v-if="autoTranslate && locales.length > 1" split variant="blue" split-variant="blue" size="sm" dropup
                    @click="translateMissing"
                    :disabled="isTranslatingAll"
                    :title="'Translate missing translations|Traduire les traductions manquantes' | tr" v-b-tooltip
                    class="translate-all-btn mr-auto">
                    <template #button-content>
                        <i v-if="isTranslatingAll" class="fas fa-spinner fa-spin"></i>
                        <i v-else class="fas fa-language"></i>
                        <span class="ml-1">{{ 'Translate Missing|Traductions manquantes' | tr }}</span>
                    </template>
                    <b-dropdown-item @click="translateAll">
                        <i class="fas fa-language mr-1"></i>
                        <span v-tr>Translate All (Replace)|Traduire tout (Remplacer)</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="checkTranslation">
                        <i class="fas fa-language mr-1"></i>
                        <span v-tr>Check Translation|Vérifier la Traduction</span>
                    </b-dropdown-item>
                </b-dropdown>
                <b-btn variant="white" @click="extendedModalEnabled = false"><span v-tr>Close|Fermer</span></b-btn>
            </template>
        </b-modal>

        <b-modal v-if="autoCompletion" v-model="autoCompletionVisible" :title="'Suggestions|Suggestions' | tr"
            scrollable body-class="pt-0" hide-footer>
            <div class="auto-complete">
                <div class="bg-white py-3 auto-complete-search-container">
                    <e-input ref="autoCompleteSearch" v-model="autoCompleteSearch" class="auto-complete-search"
                        state="disabled" debounce @enter="onSubmitAutoCompletion" type="text"
                        :placeholder="'Search|Rechercher' | tr" />
                </div>
                <b-list-group class="compact auto-completion-list">
                    <b-list-group-item v-for="(item, index) in autoCompletionFiltered"
                        @click="selectAutoCompletion(item)" :key="'complete-' + index"
                        :class="{ 'active': autoCompletionFiltered.length === 1 }">
                        {{ item | tr }}
                    </b-list-group-item>
                </b-list-group>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { translate, getTranslations, setLocale, getSupportedLocales, getShortCodeFromFull, normalizeLocaleCode, getLanguageObject } from "@/../vue-components/helpers/I18nPluginHelper.js";
import { isStringEmpty } from "../helpers/isStringEmpty.js";
import Network from "../helpers/Network.js";
import EBtnLanguage from "@/../vue-components/components/e-btn-language.vue";
import { getLanguage as legacyGetLanguage } from "@/../vue-components/helpers/i18n";

export default {
    name: `e-translation-input`,
    components: { EBtnLanguage },
    props: {
        value: {},
        autoCompletion: { type: Array },
        type: { type: String, default: `text` },
        label: { type: String },
        allowEmpty: { type: Boolean, default: true },
        placeholder: {},
        forceLang: { type: String, default: null },
        inline: {},
        disabled: {},
        readonly: { type: Boolean, default: false },
        height: { default: `100px` },
        noMargin: { type: Boolean },
        expandedHeight: { default: `100px` },
        size: { type: String },
        state: { type: String },
        maxlength: { type: [String, Number] },
        debounce: { type: Number, default: 0 },
        autoTranslate: { type: Boolean, default: true },
        useLocales: { type: Array, default: null }
    },
    data() {
        return {
            extended: false,
            extendedModalEnabled: false,
            timeout: null,
            autoCompleteSearch: ``,
            autoCompletionVisible: false,
            translatingLocales: {},
            translationCheckPreviews: null,
            sourceLanguage: null
        }
    },
    mounted() {
        this.sourceLanguage = this.currentLanguage.locale;
    },
    computed: {
        isLegacyMode() {
            return this.useLocales == null || this.useLocales.length === 0;
        },
        currentLanguage() {
            let lang = this.$store.state.cms?.previewLanguage || this.$store.state.previewLanguage;
            if (this.forceLang) {
                lang = this.forceLang;
            } else if (this.isLegacyMode && !lang) {
                lang = legacyGetLanguage();
            }

            return getLanguageObject(lang);
        },
        locales() {
            const supported = getSupportedLocales();
            const codes = this.isLegacyMode ? [`en`, `fr`] : this.useLocales;

            return codes.map(code => supported.find(l => l.locale === code)).filter(Boolean);
        },
        showModalToggle() {
            return this.locales.length > 1;
        },
        autoCompletionFiltered() {
            if (this.autoCompletion && !isStringEmpty(this.autoCompleteSearch)) {
                const regexp = new RegExp(this.autoCompleteSearch, `i`);
                return this.autoCompletion.filter(item => regexp.test(translate(item, this.currentLanguage.locale)));
            } else {
                return this.autoCompletion;
            }
        },
        isValid() {
            if (this.allowEmpty) {
                return true;
            }
            return this.locales.every(locale => !isStringEmpty(this.getLocaleValue(locale.locale)));
        },
        hasAutoTranslateSource() {
            return !isStringEmpty(this.getLocaleValue(this.currentLanguage.locale));
        },
        isTranslatingAll() {
            return this.locales.slice(1).every(locale => this.translatingLocales[locale.locale]);
        },
        sourceLanguageObject() {
            return this.locales.find(l => l.locale === this.sourceLanguage) || this.currentLanguage;
        },
        languageOptions() {
            return this.locales.map(locale => ({
                value: locale.locale,
                text: `${locale.name} (${locale.locale})`
            }));
        }
    },
    methods: {
        getLocaleValue(localeCode) {
            const translations = getTranslations(this.value);
            if (!translations) return typeof this.value === `string` ? this.value : ``;

            const value = translations[localeCode] || translations[normalizeLocaleCode(localeCode)] || translations.default;
            return value != null && typeof value !== `object` ? String(value) : ``;
        },
        localeState(localeCode) {
            if (this.state === `disabled`) return null;
            if (!this.extended) return this.isValid;
            return this.allowEmpty || !isStringEmpty(this.getLocaleValue(localeCode));
        },
        placeholderForLocale(locale) {
            if (this.placeholder) {
                const code = locale.locale.toString().toUpperCase();
                const base = translate(this.placeholder, locale.locale);
                return code ? `${base} (${code})` : base;
            } else {
                const name = locale.name || locale.locale;
                return name ? `(${name})` : ``;
            }
        },
        openModal() {
            this.translationCheckPreviews = null;
            this.extended = true;
            this.extendedModalEnabled = true;
        },
        setPreviewLanguage(locale) {
            if (this.forceLang || !locale) return;

            // Get language object from locale code
            const language = getLanguageObject(locale);
            if (!language) return;

            if (this.isLegacyMode) {
                // Legacy mode: use old i18n system
                this.$set(this.$store.state, `previewLanguage`, language.locale);
            } else {
                // CMS mode: use new I18nPlugin
                setLocale(language.locale);

                // Update cms store if it exists (for cms context)
                if (this.$store.state.cms) {
                    this.$set(this.$store.state.cms, `previewLanguage`, language.locale);
                }
                // Also update root store for backward compatibility
                if (this.$store.state.previewLanguage) {
                    this.$set(this.$store.state, `previewLanguage`, language.locale);
                }
            }
        },
        onInput(localeCode, val) {
            if (this.timeout) clearTimeout(this.timeout);

            const translations = getTranslations(this.value) || {};
            translations[localeCode] = val;

            const sourceLanguage = this.sourceLanguage || this.currentLanguage.locale;
            if (localeCode === sourceLanguage) {
                this.translationCheckPreviews = null;
            } else if (this.translationCheckPreviews && Object.prototype.hasOwnProperty.call(this.translationCheckPreviews, localeCode)) {
                this.$delete(this.translationCheckPreviews, localeCode);
            }

            this.timeout = setTimeout(() => this.$emit(`input`, translations), this.debounce);
        },
        setSourceLanguage(locale) {
            this.sourceLanguage = locale;
        },
        hasTranslationCheckPreview(localeCode) {
            return !!this.translationCheckPreviews
                && Object.prototype.hasOwnProperty.call(this.translationCheckPreviews, localeCode);
        },
        translateLocale(locale) {
            const sourceLang = this.sourceLanguage || this.currentLanguage.locale;
            const sourceText = this.getLocaleValue(sourceLang);
            if (isStringEmpty(sourceText)) {
                this.$toast.error(translate(`Please enter text in the source language first (${sourceLang})|Veuillez d'abord saisir du texte dans la langue source (${sourceLang})`, this.currentLanguage.locale));
                return;
            }

            this.$set(this.translatingLocales, locale.locale, true);
            this.translateWithAI(sourceText, sourceLang, locale.locale)
                .finally(() => this.$set(this.translatingLocales, locale.locale, false));
        },
        checkTranslation() {
            const sourceLang = this.sourceLanguage || this.currentLanguage.locale;
            const targetLocales = this.locales.filter(l => l.locale !== sourceLang);
            this.translationCheckPreviews = {};

            Promise.all(
                targetLocales.map(locale => {
                    const localeText = this.getLocaleValue(locale.locale);
                    if (isStringEmpty(localeText)) {
                        return Promise.resolve();
                    }

                    this.$set(this.translatingLocales, locale.locale, true);
                    return this.translatePreview(localeText, locale.locale, sourceLang)
                        .then(translatedText => {
                            if (translatedText != null) {
                                this.$set(this.translationCheckPreviews, locale.locale, translatedText);
                            }
                        })
                        .finally(() => this.$set(this.translatingLocales, locale.locale, false));
                })
            );
        },
        translatePreview(text, from, to) {
            const fromShort = getShortCodeFromFull(from);
            const toShort = getShortCodeFromFull(to);

            return Network.post(`/translate`, { text, from: fromShort, to: toShort }, { system_api: true })
                .then(res => res.data)
                .catch(err => {
                    console.error(err.response?.data || err);
                    return null;
                });
        },
        translateAll() {
            const sourceLang = this.sourceLanguage || this.currentLanguage.locale;
            const sourceText = this.getLocaleValue(sourceLang);
            if (isStringEmpty(sourceText)) {
                this.$toast.error(translate(`Please enter text in the source language first (${sourceLang})|Veuillez d'abord saisir du texte dans la langue source (${sourceLang})`, this.currentLanguage.locale));
                return;
            }

            const targetLocales = this.locales.filter(l => l.locale !== sourceLang);
            targetLocales.forEach(locale => this.$set(this.translatingLocales, locale.locale, true));

            Promise.all(
                targetLocales.map(locale => this.translateWithAI(sourceText, sourceLang, locale.locale))
            ).finally(() => {
                targetLocales.forEach(locale => this.$set(this.translatingLocales, locale.locale, false));
            });
        },
        translateMissing() {
            const sourceLang = this.sourceLanguage || this.currentLanguage.locale;
            const sourceText = this.getLocaleValue(sourceLang);
            if (isStringEmpty(sourceText)) {
                this.$toast.error(translate(`Please enter text in the source language first (${sourceLang})|Veuillez d'abord saisir du texte dans la langue source (${sourceLang})`, this.currentLanguage.locale));
                return;
            }

            const targetLocales = this.locales.filter(l => l.locale !== sourceLang && isStringEmpty(this.getLocaleValue(l.locale)));
            if (targetLocales.length === 0) {
                this.$toast.info(translate(`All translations are already filled|Toutes les traductions sont déjà remplies`, this.currentLanguage.locale));
                return;
            }

            targetLocales.forEach(locale => this.$set(this.translatingLocales, locale.locale, true));

            Promise.all(
                targetLocales.map(locale => this.translateWithAI(sourceText, sourceLang, locale.locale))
            ).finally(() => {
                targetLocales.forEach(locale => this.$set(this.translatingLocales, locale.locale, false));
            });
        },
        translateWithAI(text, from, to) {
            const fromShort = getShortCodeFromFull(from);
            const toShort = getShortCodeFromFull(to);

            return Network.post(`/translate`, { text, from: fromShort, to: toShort }, { system_api: true })
                .then(res => this.onInput(to, res.data))
                .catch(err => {
                    console.error(err.response?.data || err);
                    return text;
                });
        },
        showAutoCompletion() {
            this.autoCompletionVisible = true;
            setTimeout(() => {
                this.$refs.autoCompleteSearch.select();
            }, 150);
        },
        selectAutoCompletion(value) {
            if (!value) return;

            const translations = getTranslations(this.value) || {};
            this.locales.forEach(locale => {
                translations[locale.locale] = translate(value, locale.locale);
            });
            this.$emit(`input`, translations);
            this.autoCompletionVisible = false;
        },
        onSubmitAutoCompletion() {
            if (this.autoCompletionFiltered?.length) {
                this.selectAutoCompletion(this.autoCompletionFiltered[0]);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.modal {
    .auto-complete {
        .auto-complete-search-container {
            position: sticky;
            top: 0;
            z-index: 1;
        }

        .auto-completion-list {
            width: 100%;
        }
    }
}
</style>

<style lang="scss" scoped>

.translate-input {
    position: relative;

    .translate-btn {
        min-width: auto !important;
    }
    .modal-toggle-btn {
        min-width: auto !important;
    }

    .dropdown-menu {
        background-color: transparent;
        border: none;
        color: inherit;
        padding: 0;
        width: 100%;
    }

    .locale-code {
        position: absolute;
        left: -1px;
        top: -22px;

        padding: 2px 6px;
        background: #e9ecef;
        border-radius: 0.25rem 0.25rem 0 0;
        font-size: 0.85rem;
        font-weight: 600;
    }

    .input-container .input-group {
        display: flex;
        align-items: stretch;
        padding: 0;

        .form-control:first-child {
            border-radius: 0.25rem 0 0 0.25rem;
        }

        .form-control:last-child {
            border-radius: 0 0.25rem 0.25rem 0;
        }
    }
}

:deep(.flag-container) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    position: relative;
    cursor: default;

    width: 46px;
    border-bottom-left-radius: 0.25rem;

    .fas {
        position: absolute;
        right: 2px;
        bottom: 2px;
    }

    img {
        min-width: 20px;
        transition: transform 100ms;
    }

    &.clickable {
        cursor: pointer;

        &:hover {
            background-color: #ddd;

            img {
                transform: scale(1.2);
            }
        }

        &:active {
            background-color: #ccc;

            img {
                transform: scale(0.85);
            }
        }
    }
}

:deep(.locale-input+.locale-input) {
    margin-top: 16px;
}

.translation-check-preview {
    .form-control:disabled {
        background-color: #f8f9fa;
        opacity: 1;
    }
}
</style>
